<template>
  <section
    ref="$block"
    :class="{
      'osk-blocks-text-controls': true,
      'osk-blocks-text-controls_no-image': !data.imageUrl,
      'osk-blocks-text-controls_only-image': onlyImage,
      'osk-blocks-text-controls_full': data.imageUrl && !onlyImage,
    }"
  >
    <div class="osk-wrapper">
      <template
        v-if="data"
      >
        <div
          v-if="data.title"
          class="osk-blocks-text-controls__title"
        >
          {{ data.title }}
        </div>

        <img
          v-if="data.imageUrl"
          :src="data.imageUrl"
          class="osk-blocks-text-controls__image"
          alt="text"
        >

        <div
          :class="{
            'osk-blocks-text-controls__right': true,
            'osk-blocks-text-controls__right_no-image': !data.imageUrl,
          }"
        >
          <div
            v-if="data.title"
            class="osk-blocks-text-controls__title-desktop"
          >
            {{ data.title }}
          </div>

          <div
            v-if="data.text"
            class="osk-blocks-text-controls__text"
          >
            <img
              v-if="data.userId"
              class="osk-blocks-text-controls__text-image"
              src="~/assets/icons/Quotes.svg"
            >
            {{ data.text }}
          </div>

          <div
            v-if="data.userId && !onlyImage"
            class="osk-blocks-text-controls__user"
          >
            <img
              v-if="data.userAvatarUrl"
              class="osk-blocks-text-controls__user-img"
              :src="data.userAvatarUrl"
            >

            <div
              v-else
              class="osk-blocks-text-controls__user-no-img"
            >
              {{ firstCharComp }}
            </div>

            <div class="osk-blocks-text-controls__user-right">
              <div class="osk-blocks-text-controls__user-name">
                {{ data.userName }}
              </div>

              <div class="osk-blocks-text-controls__user-text">
                {{ data.userSubtitle }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { PrimaryContentDTO, TextControlViewDTO } from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const props = defineProps<{
  data: PrimaryContentDTO
}>();

const $block = ref<ComponentPublicInstance>();

const { $addEvent } = useNuxtApp();

useScrollEvent($block, event);

const data = computed(() => props.data.content as TextControlViewDTO);

const firstCharComp = computed(() => data.value.userId?.slice(0, 1) ?? 'U');

const onlyImage = computed(() => data.value.imageUrl && !data.value.text && !data.value.title);

function event() {
  $addEvent(AnalyticsEvents.SwiperBlockView, {
    chapter: props.data.contentType,
    chapter_index: props.data.chapterIndex,
    segment_id: props.data.segmentId,
    id_chapter: props.data.id,
  });
}
</script>

<style scoped lang="scss">
@import "~/assets/scss/settings/index";

.osk-blocks-text-controls {
  padding-bottom: 42px;

  @include media-query(lg-and-up) {
    padding-bottom: 82px;
  }

  &__title {
    @include font-style($font-size-heading, normal, $font-weight-bold);
    margin-bottom: 18px;

    @include media-query(lg-and-up) {
      display: none;
    }
  }

  .osk-wrapper {
    @include media-query(lg-and-up) {
      display: flex;
      min-height: 470px;
    }
  }

  &_no-image {
    .osk-wrapper {
      @include media-query(lg-and-up) {
        min-height: auto;
      }
    }

    .osk-blocks-text-controls__title-desktop {
      @include media-query(lg-and-up) {
        padding-top: 10px;
      }
    }
  }

  &__title-desktop {
    display: none;

    @include media-query(lg-and-up) {
      display: block;
      @include font-style($font-size-headline, normal, $font-weight-bold);
      margin-bottom: 40px;
    }
  }

  &__image {
    display: block;
    width: 100%;
    height: auto;

    @include media-query(lg-and-up) {
      width: 50%;
      margin-bottom: 0;
      object-fit: cover;
      border-radius: 4px 0 0 4px;
    }
  }

  &__right {
    @include media-query(lg-and-up) {
      width: 50%;
      background: $grey-4;
      padding: 24px 60px;
      border-radius: 0 4px 4px 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    &_no-image {
      @include media-query(lg-and-up) {
        width: 100%;
        background: none;
        text-align: center;
        max-width: 778px;
        margin: 0 auto;
        padding: 0;

        .osk-blocks-text-controls__user {
          text-align: left;
          margin: 0 auto;
        }

        .osk-blocks-text-controls__title-desktop {
          font-size: 18px;
          font-weight: 700;
          line-height: normal;
          max-width: 477px;
          margin: 0 auto 24px;
        }
      }
    }
  }

  &__text {
    position: relative;
    line-height: $line-height-subheading;
    letter-spacing: -0.408px;
  }

  &__text-image {
    position: absolute;
    left: 1px;
    top: -28px;
  }

  &__user {
    display: flex;
    align-items: center;
    margin-top: 24px;

    @include media-query(lg-and-up) {
      margin-top: 24px;
    }
  }

  &__user-img {
    width: 40px;
    height: 40px;
    border-radius: 40px;
    margin-right: 12px;

    @include media-query(lg-and-up) {
      width: 32px;
      height: 32px;
    }
  }

  &__user-no-img {
    width: 40px;
    height: 40px;
    border-radius: 40px;
    background: #000;
    margin-right: 12px;
    color: #fff;
    display: flex;
    user-select: none;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    font-size: $font-size-subheading;
    font-weight: $font-weight-bold;
    text-transform: uppercase;
  }

  &__user-name {
    @include font-style($font-size-subheading, normal, $font-weight-medium);
    margin-bottom: 4px;

    @include media-query(lg-and-up) {
      font-size: 12px;
      font-weight: 500;
      line-height: 16px;
      margin-bottom: 2px;
    }
  }

  &__user-text {
    @include font-style($font-size-caption, normal, $font-weight-medium);
    color: $grey;

    @include media-query(lg-and-up) {
      line-height: 16px;
    }
  }

  &_only-image {
    .osk-wrapper {
      @include media-query(lg-and-up) {
        justify-content: center;
        padding: 0 12px;
      }
    }

    .osk-blocks-text-controls__image {
      max-height: 756px;
      width: auto;
    }

    .osk-blocks-text-controls__right {
      display: none;
    }
  }

  & + :deep(.osk-home-gender-page__wrapper),
  & + .osk-blocks-text-controls_only-image {
    @include media-query(lg-and-up) {
      margin-top: -40px;
    }
  }

  &_full {
    .osk-blocks-text-controls__right {
      padding-top: 44px;

      @include media-query(lg-and-up) {
        padding: 24px 60px;
      }
    }
  }
}

.osk-blocks-text-controls_only-image + .osk-blocks-text-controls_no-image {
  @include media-query(lg-and-up) {
    margin-top: -40px;
  }
}

.osk-blocks-text-controls_only-image + .osk-blocks-text-controls_only-image {
  margin-top: -26px;

  @include media-query(lg-and-up) {
    margin-top: 0;
  }
}

.osk-home-gender-page__wrapper {
  .osk-blocks-text-controls_only-image {
    @include media-query(lg-and-up) {
      margin-top: 0;
    }

    .osk-wrapper {
      padding: 0;

      @include media-query(lg-and-up) {
        padding: 0 12px;
      }
    }
  }
}

.osk-home-gender-page_isJournalMode {
  .osk-blocks-text-controls__text {
    @include font-style($font-size-subheading, 22px, $font-weight-medium);

    @include media-query(lg-and-up) {
      @include font-style($font-size-caption, 19px, $font-weight-medium);
    }

    & + .osk-blocks-text-controls__user {
      @include media-query(lg-and-up) {
        margin-top: 32px;
      }
    }
  }

  .osk-blocks-text-controls_full {
    .osk-blocks-text-controls__text {
      @include media-query(lg-and-up) {
        @include font-style($font-size-subheading, 22px, $font-weight-medium);
      }
    }
  }

  .osk-blocks-text-controls + :deep(.osk-home-gender-page__wrapper) {
    .osk-blocks-text-controls_only-image:first-child {
      margin-top: -18px;

      @include media-query(lg-and-up) {
        margin-top: 0;
      }
    }
  }

  .osk-blocks-text-controls_no-image + .osk-blocks-text-controls_full {
      margin-top: -24px;

      @include media-query(lg-and-up) {
        margin-top: 0;
      }
  }

  .osk-home-blocks-hselection + .osk-blocks-text-controls_no-image,
  .osk-filterable-shelf-block + .osk-blocks-text-controls_no-image {
    margin-top: -10px;

    @include media-query(lg-and-up) {
      margin-top: 0;
    }
  }

  .osk-blocks-text-controls_no-image + .osk-blocks-text-controls_only-image {
    margin-top: -18px;

    @include media-query(lg-and-up) {
      margin-top: -40px;
    }
  }

  .osk-blocks-text-controls__image {
    @include media-query(lg-and-up) {
      height: 470px;
    }
  }

  .osk-blocks-text-controls_full .osk-blocks-text-controls__right {
    @include media-query(lg-and-up) {
      padding: 24px 52px;
    }
  }

  .osk-blocks-text-controls__user-img {
    @include media-query(lg-and-up) {
      width: 40px;
      height: 40px;
    }
  }

  .osk-blocks-text-controls__user-name {
    @include media-query(lg-and-up) {
      font-size: 16px;
      font-weight: 600;
      line-height: normal;
    }
  }

  .osk-blocks-text-controls__user-text {
    @include media-query(lg-and-up) {
      line-height: normal;
    }
  }

  .osk-blocks-text-controls_no-image {
    .osk-blocks-text-controls__title {
      margin-bottom: 24px;
    }
  }
}
</style>
